import { useTranslations } from "next-intl";
import * as z from "zod";

export const PASSWORD_MIN_LENGTH = 10;
export const FIRST_AND_LAST_NAME_MIN_LENGTH = 2;
export const FIRST_AND_LAST_NAME_MAX_LENGTH = 40;

const passwordSchema = (t: ReturnType<typeof useTranslations<"auth">>) =>
    z
        .string()
        .min(PASSWORD_MIN_LENGTH, { message: t("common.error.passwordTooShort", { min: PASSWORD_MIN_LENGTH }) })
        .regex(/[a-z]/, { message: t("common.error.passwordNeedLowercase") })
        .regex(/[A-Z]/, { message: t("common.error.passwordNeedUppercase") })
        .regex(/[0-9]/, { message: t("common.error.passwordNeedNumber") })
        .regex(/[^a-zA-Z0-9]/, { message: t("common.error.passwordNeedSymbol") });

const emailSchema = (t: ReturnType<typeof useTranslations<"auth">>) => z.string().email({ message: t("common.error.emailRequired") });

const firstNameSchema = (t: ReturnType<typeof useTranslations<"auth">>) =>
    z
        .string()
        .min(FIRST_AND_LAST_NAME_MIN_LENGTH, { message: t("common.error.firstNameTooShort", { min: FIRST_AND_LAST_NAME_MIN_LENGTH }) })
        .max(FIRST_AND_LAST_NAME_MAX_LENGTH, { message: t("common.error.firstNameTooLong", { max: FIRST_AND_LAST_NAME_MAX_LENGTH }) });

const lastNameSchema = (t: ReturnType<typeof useTranslations<"auth">>) =>
    z
        .string()
        .min(FIRST_AND_LAST_NAME_MIN_LENGTH, { message: t("common.error.lastNameTooShort", { min: FIRST_AND_LAST_NAME_MIN_LENGTH }) })
        .max(FIRST_AND_LAST_NAME_MAX_LENGTH, { message: t("common.error.lastNameTooLong", { max: FIRST_AND_LAST_NAME_MAX_LENGTH }) });

export const RegisterSchema = (t: ReturnType<typeof useTranslations<"auth">>) =>
    z.object({
        firstName: firstNameSchema(t),
        lastName: lastNameSchema(t),
        email: emailSchema(t),
        password: passwordSchema(t),
    });

export type TRegisterSchema = z.infer<ReturnType<typeof RegisterSchema>>;

export const LoginSchema = (t: ReturnType<typeof useTranslations<"auth">>) =>
    z.object({
        email: emailSchema(t),
        password: z.string({ message: t("common.error.passwordRequired") }),
        code: z.optional(z.string().min(6, { message: t("2FA.error.2FAcodeRequired") })),
    });

export type TLoginSchema = z.infer<ReturnType<typeof LoginSchema>>;

export const ForgotPasswordSchema = (t: ReturnType<typeof useTranslations<"auth">>) =>
    z.object({
        email: emailSchema(t),
    });

export type TForgotPasswordSchema = z.infer<ReturnType<typeof ForgotPasswordSchema>>;

export const ResetPasswordSchema = (t: ReturnType<typeof useTranslations<"auth">>) =>
    z.object({
        password: passwordSchema(t),
    });

export type TResetPasswordSchema = z.infer<ReturnType<typeof ResetPasswordSchema>>;

export const SettingsSchema = (t: ReturnType<typeof useTranslations<"auth">>) =>
    z.object({
        firstName: z.optional(firstNameSchema(t)),
        lastName: z.optional(lastNameSchema(t)),
        isTwoFactorEnabled: z.optional(z.boolean()),
        password: z.string({ message: t("common.error.passwordRequired") }),
        newPassword: z.optional(passwordSchema(t)),
    });

export type TSettingsSchema = z.infer<ReturnType<typeof SettingsSchema>>;

export const ExpenseSchema = (t: ReturnType<typeof useTranslations<"expenses">>) =>
    z.object({
        date: z.date({ message: t("error.dateRequired") }).refine(
            date => {
                const currentYear = new Date().getFullYear();
                const minDate = new Date(currentYear - 5, 0, 1);
                const maxDate = new Date(currentYear + 5, 11, 31);

                return date >= minDate && date <= maxDate;
            },
            { message: t("error.dateOutOfRange", { min: new Date().getFullYear() - 5, max: new Date().getFullYear() + 5 }) },
        ),
        amount: z.preprocess(
            val => parseFloat(val as string),
            z.number({ message: t("error.amountRequired") }).gt(0, { message: t("error.amountMustBePositive") }),
        ),
        journalId: z.string({ message: t("error.journalIdRequired") }),
        movedAccountId: z.string({ message: t("error.movedAccountIdRequired") }),
        pieceNumber: z.optional(z.string()),
        object: z.optional(z.string()),
        folderId: z.optional(z.string()),
        signature: z.optional(z.string()),
        currencyId: z.string({ message: t("error.currencyIdRequired") }),
    });

export type TExpenseSchema = z.infer<ReturnType<typeof ExpenseSchema>>;

export const FolderSchema = (t: ReturnType<typeof useTranslations<"invoiceFolders.folders">>) =>
    z.object({
        name: z.string({ message: t("error.nameRequired") }).regex(/^[a-zA-Z0-9_-]+$/, { message: t("error.nameInvalidCharacters") }),
        parentId: z.string().optional(),
    });

export type TFolderSchema = z.infer<ReturnType<typeof FolderSchema>>;

export const DeleteFolderSchema = (t: ReturnType<typeof useTranslations<"invoiceFolders.folders.delete">>) =>
    z.object({
        id: z.string(),
        password: z.string({ message: t("error.passwordRequired") }),
        deleteMode: z.enum([ "transfer", "delete" ], {
            message: t("error.deleteModeRequired"),
        }),
    });

export type TDeleteFolderSchema = z.infer<ReturnType<typeof DeleteFolderSchema>>;

export const MoveFolderSchema = () =>
    z.object({
        folderId: z.string(),
        targetFolderId: z.string().nullable(),
    });

export type TMoveFolderSchema = z.infer<ReturnType<typeof MoveFolderSchema>>;

export const MoveExpenseSchema = () =>
    z.object({
        expenseId: z.string(),
        targetFolderId: z.string().nullable(),
    });

export type TMoveExpenseSchema = z.infer<ReturnType<typeof MoveExpenseSchema>>;

export const DeleteExpenseSchema = (t: ReturnType<typeof useTranslations<"expense.delete">>) =>
    z.object({
        id: z.string(),
        password: z.string({ message: t("error.passwordRequired") }),
    });

export type TDeleteExpenseSchema = z.infer<ReturnType<typeof DeleteExpenseSchema>>;